// Drives the free-play loop: bot turns (timed so the human can follow), and the
// per-discard claim window. It owns no rendering — the scene supplies `FlowHooks`
// to read/write state and to surface the local player's turn / claim UI.
// Claim window rule (from the plan): bots decide instantly, but if the LOCAL seat has
// any claim option the loop PAUSES on real human input — even though bots have already
// voiced their claims — and only resolves priority once the human taps a claim or Skip.
// When the local seat has no stake, bot claims resolve after a short delay instead.

import {
  canTsumo,
  claimExposedKong,
  claimsFor,
  declareTsumo,
  discardTile,
  type HandResult,
  type MahjongState,
} from '../game/GameState.js';
import type { Claim, SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { AiStrategy, KongDecision } from '../ai/types.js';
import { applyBotTurnAction, chooseBotTurnAction, collectBotClaims } from '../ai/botTurn.js';
import type { Rng } from '../utils/prng.js';
import { BOT_THINK_DELAY_MS, CLAIM_RESOLVE_DELAY_MS } from '../utils/constants.js';
import { LOCAL_SEAT } from './Bootstrap.js';
import { BotTimer } from './BotTimer.js';
import { applyOwnTurnKong, ownTurnKongOptions, tryBotExposedKong } from './BotKong.js';
import { resolveClaims } from './ClaimResolution.js';

export interface FlowHooks {
  readonly getState: () => MahjongState;
  readonly setState: (next: MahjongState) => void;
  /** Local seat on turn to discard: `canDeclareTsumo` offers the win, `kongOptions` any kong. */
  readonly onLocalTurn: (canDeclareTsumo: boolean, kongOptions: readonly KongDecision[]) => void;
  /** The local seat may claim the open discard — block until it acts. */
  readonly onLocalClaimWindow: (claims: SeatClaims) => void;
  readonly onHandEnd: (result: HandResult) => void;
  readonly clearLocalUi: () => void;
}

export class BotDriver {
  private readonly timer = new BotTimer();
  private pendingBotClaims: Claim[] = [];

  constructor(
    private readonly strategy: AiStrategy,
    private readonly rng: Rng,
    private readonly hooks: FlowHooks,
  ) {}

  /**
   * Freeze or resume the timed half of the loop (see `BotTimer`). Nothing else
   * needs freezing: the human's own moves come in through this class's public
   * methods, and whatever asked for the pause is the surface blocking those.
   */
  setPaused(paused: boolean): void {
    this.timer.setPaused(paused);
  }

  /** Inspect the live phase and drive the next step (or hand to the human). Idempotent; call after every mutation. */
  advance(): void {
    this.timer.cancel();
    const state = this.hooks.getState();
    switch (state.phase) {
      case 'hand-end':
        this.hooks.clearLocalUi();
        if (state.result !== null) this.hooks.onHandEnd(state.result);
        return;
      case 'awaiting-discard':
        if (state.turn === LOCAL_SEAT) {
          this.hooks.onLocalTurn(canTsumo(state), ownTurnKongOptions(state, LOCAL_SEAT));
        } else {
          this.hooks.clearLocalUi();
          this.scheduleBotTurn(state.turn);
        }
        return;
      case 'claim-window':
        this.resolveClaimWindow(state);
        return;
    }
  }

  /** Human tapped a hand tile to discard it. */
  discardLocal(tileId: number): void {
    const state = this.hooks.getState();
    if (state.phase !== 'awaiting-discard' || state.turn !== LOCAL_SEAT) return;
    this.hooks.clearLocalUi();
    this.hooks.setState(discardTile(state, LOCAL_SEAT, tileId));
    this.advance();
  }

  /** Human declared a self-drawn win. */
  declareLocalTsumo(): void {
    const state = this.hooks.getState();
    if (state.turn !== LOCAL_SEAT || !canTsumo(state)) return;
    this.hooks.clearLocalUi();
    this.hooks.setState(declareTsumo(state, LOCAL_SEAT));
    this.advance();
  }

  /** Human resolved their claim window — `choice` is their claim or null (Skip). */
  resolveLocalClaim(choice: Claim | null): void {
    this.hooks.clearLocalUi();
    const claims = [...this.pendingBotClaims];
    this.pendingBotClaims = [];
    if (choice !== null) claims.push(choice);
    this.applyClaimResolution(claims);
  }

  /** Human declared a concealed or added kong on their own turn. */
  declareLocalKong(kong: KongDecision): void {
    const state = this.hooks.getState();
    if (state.turn !== LOCAL_SEAT || state.phase !== 'awaiting-discard') return;
    this.hooks.clearLocalUi();
    this.hooks.setState(applyOwnTurnKong(state, LOCAL_SEAT, kong)); // reuse T17b's helper — same function bots use
    this.advance();
  }

  /** Human declared an exposed kong on the open discard during a claim window. */
  resolveLocalExposedKong(): void {
    const state = this.hooks.getState();
    if (state.phase !== 'claim-window') return;
    this.hooks.clearLocalUi();
    const pending = [...this.pendingBotClaims];
    this.pendingBotClaims = [];
    if (pending.some((c) => c.type === 'ron')) {
      // A bot's already-pending Ron outranks the human's kong attempt — resolve
      // normally (same as if the human had tapped Skip), don't let kong win.
      this.applyClaimResolution(pending);
      return;
    }
    this.hooks.setState(claimExposedKong(state, LOCAL_SEAT));
    this.advance();
  }

  dispose(): void {
    this.timer.cancel();
    this.pendingBotClaims = [];
  }

  private scheduleBotTurn(seat: Seat): void {
    this.timer.schedule(() => {
      const state = this.hooks.getState();
      if (state.phase !== 'awaiting-discard' || state.turn !== seat) return;
      const action = chooseBotTurnAction(state, seat, this.strategy, this.rng);
      this.hooks.setState(applyBotTurnAction(state, seat, action));
      this.advance();
    }, BOT_THINK_DELAY_MS);
  }

  private resolveClaimWindow(state: MahjongState): void {
    const seatClaims = claimsFor(state);
    const local = seatClaims.find((c) => c.seat === LOCAL_SEAT) ?? null;
    // Scope boundary (deliberate — see plan T17b): a bot's exposed kong only
    // pre-empts Pung/Chi when the local seat has no claim here (bot claims
    // resolve unattended below); with a local claim on this discard we skip
    // this and fall back to the pre-existing chooseClaim path instead.
    if (local === null) {
      const kongState = tryBotExposedKong(seatClaims, state, this.strategy);
      if (kongState !== null) {
        this.hooks.setState(kongState);
        this.advance();
        return;
      }
    }
    const botClaims = collectBotClaims(
      seatClaims,
      state,
      this.strategy,
      (seat) => seat !== LOCAL_SEAT,
    );
    if (local !== null) {
      this.pendingBotClaims = botClaims;
      this.hooks.onLocalClaimWindow(local);
      return;
    }
    this.timer.schedule(() => this.applyClaimResolution(botClaims), CLAIM_RESOLVE_DELAY_MS);
  }

  private applyClaimResolution(claims: readonly Claim[]): void {
    const state = this.hooks.getState();
    const next = resolveClaims(state, claims);
    if (next === null) return;
    this.hooks.setState(next);
    this.advance();
  }
}
